import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Scale, Brain, Flame, Layers, Compass, TrendingUp, Shield, Network, Target,
  ChevronRight, ChevronLeft, Check, Circle, Loader2, BookOpen, AlertTriangle,
  CheckCircle2, ArrowRight, Lock, RefreshCw
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  scale: Scale, brain: Brain, flame: Flame, layers: Layers,
  compass: Compass, "trending-up": TrendingUp, shield: Shield,
  network: Network, target: Target,
};

const RISK_STYLES = {
  high: { bg: "bg-red-500/10", border: "border-red-500/40", text: "text-red-400", icon: AlertTriangle },
  targeted: { bg: "bg-amber-500/10", border: "border-amber-500/40", text: "text-amber-400", icon: AlertTriangle },
  stable: { bg: "bg-emerald-500/10", border: "border-emerald-500/40", text: "text-emerald-400", icon: CheckCircle2 },
};

const LEVEL_STYLES = {
  High: { bar: "bg-emerald-500", text: "text-emerald-400" },
  Medium: { bar: "bg-amber-500", text: "text-amber-400" },
  Low: { bar: "bg-red-500", text: "text-red-400" },
  "N/A": { bar: "bg-gray-500", text: "text-gray-400" },
};

// ============ Sub Components ============

function SliderQuestion({ question, value, onChange }) {
  const min = question.min || 1;
  const max = question.max || 10;
  const current = value ?? Math.floor((min + max) / 2);

  return (
    <div className="space-y-3">
      <div className="flex justify-between text-xs text-chakra-crown/50">
        <span>{question.label_low}</span>
        <span>{question.label_high}</span>
      </div>
      <input
        type="range" min={min} max={max} step={1} value={current}
        onChange={e => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-cosmic-deep rounded-full appearance-none cursor-pointer accent-divine-gold"
        data-testid={`slider-${question.qid}`}
      />
      <div className="text-center">
        <span className="text-2xl font-heading text-divine-gold">{current}</span>
        <span className="text-chakra-crown/40 text-sm ml-1">/ {max}</span>
      </div>
    </div>
  );
}

function McqQuestion({ question, value, onChange }) {
  return (
    <div className="space-y-2">
      {question.options.map((opt, i) => {
        const isSelected = value === opt.value;
        return (
          <button key={i} onClick={() => onChange(opt.value)}
            className={`w-full text-left p-3 rounded-lg border transition-all ${
              isSelected
                ? "bg-divine-gold/15 border-divine-gold text-divine-gold"
                : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/70 hover:border-divine-gold/30"
            }`}
            data-testid={`mcq-${question.qid}-opt-${i}`}
          >
            <div className="flex items-center gap-3">
              {isSelected ? <CheckCircle2 className="w-4 h-4 text-divine-gold shrink-0" /> : <Circle className="w-4 h-4 text-chakra-crown/30 shrink-0" />}
              <span className="text-sm">{opt.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function TextQuestion({ question, value, onChange }) {
  return (
    <textarea
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder="Share your thoughts..."
      rows={3}
      className="w-full p-3 rounded-lg bg-cosmic-deep/50 border border-divine-gold/10 text-chakra-crown placeholder-chakra-crown/30 focus:border-divine-gold/40 focus:outline-none resize-none text-sm"
      data-testid={`text-${question.qid}`}
    />
  );
}

function QuestionRenderer({ question, value, onChange }) {
  if (question.type === "awareness_slider" || question.type === "awareness_slider_5") {
    return <SliderQuestion question={question} value={value} onChange={onChange} />;
  }
  if (question.type === "awareness_mcq" || question.type === "alignment_mcq") {
    return <McqQuestion question={question} value={value} onChange={onChange} />;
  }
  return <TextQuestion question={question} value={value} onChange={onChange} />;
}

function QuestionTypeTag({ type }) {
  if (type.startsWith("awareness")) return <span className="text-[10px] px-2 py-0.5 rounded-full bg-divine-gold/10 text-divine-gold border border-divine-gold/20">AWARENESS</span>;
  if (type.startsWith("alignment")) return <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 border border-sky-500/20">ALIGNMENT</span>;
  return <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-400 border border-violet-500/20">AWAKENING</span>;
}

// ============ Module Tab Button ============

function ModuleTab({ module, isActive, isCompleted, onClick }) {
  const Icon = ICON_MAP[module.icon] || Circle;
  return (
    <button onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all whitespace-nowrap text-sm ${
        isActive
          ? "bg-divine-gold/15 border border-divine-gold text-divine-gold"
          : isCompleted
            ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
            : "bg-cosmic-light/5 border border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/20"
      }`}
      data-testid={`diagnostic-module-${module.id}`}
    >
      {isCompleted && !isActive ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
      <span className="font-medium">{module.id}</span>
    </button>
  );
}

// ============ Module Card for Test Overview ============

function ModuleCard({ module, isCompleted, onClick, onRetake }) {
  const Icon = ICON_MAP[module.icon] || Circle;
  return (
    <div className={`w-full text-left p-4 rounded-xl border transition-all ${
      isCompleted
        ? "bg-emerald-500/5 border-emerald-500/20"
        : "bg-cosmic-light/5 border-divine-gold/10"
    }`} data-testid={`diagnostic-card-${module.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isCompleted ? "bg-emerald-500/10" : "bg-divine-gold/10"}`}>
          <Icon className={`w-5 h-5 ${isCompleted ? "text-emerald-400" : "text-divine-gold"}`} />
        </div>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onClick}>
          <div className="flex items-center gap-2">
            <p className={`text-sm font-heading truncate ${isCompleted ? "text-emerald-400" : "text-divine-gold"}`}>{module.name}</p>
            {isCompleted && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
          </div>
          <p className="text-xs text-chakra-crown/40 font-heading">{module.subtitle}</p>
          <p className="text-xs text-chakra-crown/50 mt-1.5 leading-relaxed line-clamp-2">{module.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <button onClick={onClick} className="p-1.5 rounded-lg hover:bg-divine-gold/10 transition-colors" data-testid={`take-quiz-${module.id}`}>
            <ChevronRight className={`w-4 h-4 ${isCompleted ? "text-emerald-400/40" : "text-divine-gold/40"}`} />
          </button>
          {isCompleted && onRetake && (
            <button onClick={(e) => { e.stopPropagation(); onRetake(module.id); }}
              className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors"
              data-testid={`retake-module-${module.id}`}>
              <RefreshCw className="w-2.5 h-2.5" /> Retake
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Score Card ============

function ScoreCard({ module, score }) {
  const Icon = ICON_MAP[module.icon] || Circle;
  const style = LEVEL_STYLES[score?.level] || LEVEL_STYLES["N/A"];
  const avg = score?.average || 0;

  return (
    <div className="sacred-card p-4" data-testid={`score-card-${module.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${style.text} bg-cosmic-deep`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-heading text-divine-gold truncate">{module.name}</p>
          <p className="text-xs text-chakra-crown/40">{module.subtitle}</p>
        </div>
        <div className="text-right">
          <span className={`text-lg font-heading ${style.text}`}>{avg.toFixed(1)}</span>
          <span className="text-xs text-chakra-crown/30">/5</span>
        </div>
      </div>
      <div className="h-2 bg-cosmic-deep rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${style.bar} transition-all duration-700`} style={{ width: `${(avg / 5) * 100}%` }} />
      </div>
      <div className="flex justify-between mt-1.5">
        <span className={`text-xs ${style.text}`}>{score?.level || "N/A"}</span>
        <span className="text-xs text-chakra-crown/30">{score?.answered || 0} items scored</span>
      </div>
    </div>
  );
}

// ============ MAIN COMPONENT ============

export default function LifeDiagnostic({ token, userId, mode = "all", onRetakeAll: onRetakeAllCallback, extraCompleted = 0, extraTotal = 0 }) {
  const diagnosticRef = useRef(null);
  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(1);
  const [questions, setQuestions] = useState({});
  const [answers, setAnswers] = useState({});
  const [completedModules, setCompletedModules] = useState([]);
  const [scores, setScores] = useState({});
  const [risk, setRisk] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [moduleRecommendations, setModuleRecommendations] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testView, setTestView] = useState("overview"); // "overview" or "quiz"

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const loadModules = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/life-diagnostic/modules`, { headers });
      const data = await res.json();
      setModules(data.modules || []);
    } catch (e) { console.error("Failed to load modules", e); }
  }, []);

  const loadResults = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/life-diagnostic/results/${userId}`, { headers });
      const data = await res.json();
      setAnswers(data.answers || {});
      setCompletedModules(data.modules_completed || []);
      setScores(data.scores || {});
      setRisk(data.risk);
      setRecommendations(data.recommendations || []);
      setModuleRecommendations(data.module_recommendations || {});
    } catch (e) { console.error("Failed to load results", e); }
  }, [userId]);

  const loadQuestions = useCallback(async (sectionId) => {
    if (questions[sectionId]) return;
    try {
      const res = await fetch(`${API}/api/life-diagnostic/questions/${sectionId}`, { headers });
      const data = await res.json();
      setQuestions(prev => ({ ...prev, [sectionId]: data.questions }));
    } catch (e) { console.error("Failed to load questions", e); }
  }, [questions]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadModules();
      await loadResults();
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (activeModule) loadQuestions(activeModule);
  }, [activeModule]);

  const setAnswer = (sectionId, qid, value) => {
    setAnswers(prev => ({
      ...prev,
      [String(sectionId)]: { ...(prev[String(sectionId)] || {}), [qid]: value },
    }));
  };

  const saveSection = async (sectionId) => {
    setSaving(true);
    try {
      const sectionAnswers = answers[String(sectionId)] || {};
      const res = await fetch(`${API}/api/life-diagnostic/save-section`, {
        method: "POST", headers,
        body: JSON.stringify({ section_id: sectionId, answers: sectionAnswers }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error("Life save-section failed:", res.status, err);
        toast.error(`Save failed (${res.status}). Please retry or contact support.`);
        setSaving(false);
        return;
      }
      const data = await res.json();
      if (data.score) {
        setScores(prev => ({ ...prev, [String(sectionId)]: data.score }));
      }
      if (!completedModules.includes(sectionId)) {
        setCompletedModules(prev => [...prev, sectionId].sort((a, b) => a - b));
      }
      // Go back to overview after save
      setTestView("overview");
      setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      console.error("Save failed", e);
      toast.error("Network error. Please retry.");
    }
    setSaving(false);
  };

  const saveAllAndFinish = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/life-diagnostic/save-all`, {
        method: "POST", headers,
        body: JSON.stringify({ answers }),
      });
      if (!res.ok) {
        const err = await res.text();
        console.error("Life save-all failed:", res.status, err);
        toast.error(`Final scoring failed (${res.status}). Sections are saved — please retry.`);
        setSaving(false);
        return;
      }
      const data = await res.json();
      if (data.scores) setScores(data.scores);
      if (data.risk) setRisk(data.risk);
      if (data.recommendations) setRecommendations(data.recommendations);
      setCompletedModules([1, 2, 3, 4, 5, 6, 7, 8, 9]);
      await loadResults();
      setTestView("overview");
      setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    } catch (e) {
      console.error("Save all failed", e);
      toast.error("Network error. Please retry.");
    }
    setSaving(false);
  };

  const handleRetakeModule = async (moduleId) => {
    // Call backend to clear this module's data
    try {
      await fetch(`${API}/api/life-diagnostic/retake-module/${moduleId}`, { method: "POST", headers });
    } catch (e) { console.error("Failed to reset module on server", e); }
    // Clear frontend state for this module
    setAnswers(prev => ({ ...prev, [String(moduleId)]: {} }));
    setCompletedModules(prev => prev.filter(m => m !== moduleId));
    setScores(prev => { const next = { ...prev }; delete next[String(moduleId)]; return next; });
    setRecommendations([]);
    setModuleRecommendations(prev => { const next = { ...prev }; delete next[String(moduleId)]; return next; });
    setActiveModule(moduleId);
    loadQuestions(moduleId);
    setTestView("quiz");
    // Notify parent to refresh downstream data
    if (onRetakeAllCallback) onRetakeAllCallback();
    setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const sectionQuestions = questions[activeModule] || [];
  const sectionAnswers = answers[String(activeModule)] || {};
  const currentModule = modules.find(m => m.id === activeModule);
  const answeredCount = sectionQuestions.filter(q => sectionAnswers[q.qid] !== undefined && sectionAnswers[q.qid] !== "").length;
  const allAnswered = answeredCount === sectionQuestions.length;
  const scoredQs = sectionQuestions.filter(q => q.type.startsWith("awareness_"));
  const scoredAnswered = scoredQs.filter(q => sectionAnswers[q.qid] !== undefined).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-divine-gold animate-spin" />
        <span className="ml-3 text-chakra-crown/50">Loading Life Diagnostic...</span>
      </div>
    );
  }

  // MODE: "test" — card grid overview + quiz drill-down
  if (mode === "test") {
    // === QUIZ VIEW: Answering questions for the active module ===
    if (testView === "quiz") {
      return (
        <div className="space-y-5" data-testid="life-diagnostic-test" ref={diagnosticRef}>
          {/* Exit bar */}
          <button
            onClick={() => { setTestView("overview"); setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
            className="flex items-center gap-2 text-sm text-chakra-crown/50 hover:text-divine-gold transition-colors"
            data-testid="exit-quiz-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to All Modules
          </button>

          <AnimatePresence mode="wait">
            {currentModule && (
              <motion.div key={`module-${activeModule}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="sacred-card" data-testid="module-header">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-divine-gold/10 flex items-center justify-center shrink-0">
                      {React.createElement(ICON_MAP[currentModule.icon] || Circle, { className: "w-6 h-6 text-divine-gold" })}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-divine-gold">{currentModule.name}</h3>
                      <p className="text-sm text-chakra-crown/40 font-heading">{currentModule.subtitle}</p>
                      <p className="text-sm text-chakra-crown/60 mt-2 leading-relaxed">{currentModule.description}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {sectionQuestions.map((q, idx) => (
                    <div key={q.qid} className="sacred-card" data-testid={`question-${q.qid}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-divine-gold/40 text-sm font-heading shrink-0 mt-0.5">Q{idx + 1}</span>
                          <p className="text-chakra-crown text-sm leading-relaxed">{q.text}</p>
                        </div>
                        <QuestionTypeTag type={q.type} />
                      </div>
                      <QuestionRenderer question={q} value={sectionAnswers[q.qid]}
                        onChange={(val) => setAnswer(activeModule, q.qid, val)} />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-chakra-crown/40">
                    {answeredCount}/{sectionQuestions.length} answered
                    {scoredQs.length > 0 && ` (${scoredAnswered}/${scoredQs.length} scored)`}
                  </span>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setTestView("overview"); setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold hover:border-divine-gold/40 text-sm transition-all"
                      data-testid="exit-quiz-bottom-btn"
                    >
                      Exit
                    </button>
                    {activeModule === 9 && completedModules.length >= 8 ? (
                      <button onClick={saveAllAndFinish} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                        data-testid="save-launch-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                        SAVE & FINISH
                      </button>
                    ) : (
                      <button onClick={() => saveSection(activeModule)} disabled={saving || scoredAnswered === 0}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                        data-testid="save-next-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        SAVE
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    // === OVERVIEW: Card grid of all 9 modules ===
    return (
      <div className="space-y-5" data-testid="life-diagnostic-test" ref={diagnosticRef}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-divine-gold">DIAGNOSTIC TEST</h2>
            <p className="text-chakra-crown/50 text-sm mt-1">9-Module Assessment: Awareness - Alignment - Awakening</p>
          </div>
          <div className="text-right">
            <span className="text-xs text-chakra-crown/40">Progress</span>
            <p className="text-divine-gold font-heading">{completedModules.length + extraCompleted}/{9 + extraTotal}</p>
          </div>
        </div>
        <Progress value={(completedModules.length + extraCompleted) / (9 + extraTotal) * 100} className="h-2" />

        <div className="grid grid-cols-1 gap-3" data-testid="module-cards-grid">
          {modules.map(m => (
            <ModuleCard key={m.id} module={m}
              isCompleted={completedModules.includes(m.id)}
              onClick={() => { setActiveModule(m.id); setTestView("quiz"); loadQuestions(m.id); setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
              onRetake={handleRetakeModule}
            />
          ))}
        </div>
      </div>
    );
  }

  // MODE: "paired" — side-by-side test cards + result cards in aligned rows
  if (mode === "paired") {
    // Quiz view (full-width)
    if (testView === "quiz") {
      return (
        <div className="space-y-5" data-testid="life-diagnostic-paired" ref={diagnosticRef}>
          <button
            onClick={() => { setTestView("overview"); setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
            className="flex items-center gap-2 text-sm text-chakra-crown/50 hover:text-divine-gold transition-colors"
            data-testid="exit-quiz-btn"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to All Modules
          </button>

          <AnimatePresence mode="wait">
            {currentModule && (
              <motion.div key={`module-${activeModule}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
                <div className="sacred-card" data-testid="module-header">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-divine-gold/10 flex items-center justify-center shrink-0">
                      {React.createElement(ICON_MAP[currentModule.icon] || Circle, { className: "w-6 h-6 text-divine-gold" })}
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-divine-gold">{currentModule.name}</h3>
                      <p className="text-sm text-chakra-crown/40 font-heading">{currentModule.subtitle}</p>
                      <p className="text-sm text-chakra-crown/60 mt-2 leading-relaxed">{currentModule.description}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  {sectionQuestions.map((q, idx) => (
                    <div key={q.qid} className="sacred-card" data-testid={`question-${q.qid}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          <span className="text-divine-gold/40 text-sm font-heading shrink-0 mt-0.5">Q{idx + 1}</span>
                          <p className="text-chakra-crown text-sm leading-relaxed">{q.text}</p>
                        </div>
                        <QuestionTypeTag type={q.type} />
                      </div>
                      <QuestionRenderer question={q} value={sectionAnswers[q.qid]}
                        onChange={(val) => setAnswer(activeModule, q.qid, val)} />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-xs text-chakra-crown/40">
                    {answeredCount}/{sectionQuestions.length} answered
                    {scoredQs.length > 0 && ` (${scoredAnswered}/${scoredQs.length} scored)`}
                  </span>
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setTestView("overview"); setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                      className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-divine-gold/20 text-chakra-crown/60 hover:text-divine-gold hover:border-divine-gold/40 text-sm transition-all"
                      data-testid="exit-quiz-bottom-btn"
                    >
                      Exit
                    </button>
                    {activeModule === 9 && completedModules.length >= 8 ? (
                      <button onClick={saveAllAndFinish} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                        data-testid="save-launch-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Target className="w-4 h-4" />}
                        SAVE & FINISH
                      </button>
                    ) : (
                      <button onClick={() => saveSection(activeModule)} disabled={saving || scoredAnswered === 0}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-divine-gold text-cosmic-void font-heading text-sm hover:bg-divine-gold-light disabled:opacity-50 transition-all"
                        data-testid="save-next-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        SAVE
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    // Paired overview — side by side rows
    return (
      <div className="space-y-4" data-testid="life-diagnostic-paired" ref={diagnosticRef}>
        {/* Header row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-divine-gold">A. DIAGNOSTIC TESTS</h2>
              <span className="text-divine-gold font-heading text-sm">{completedModules.length + extraCompleted}/{9 + extraTotal}</span>
            </div>
            <p className="text-chakra-crown/50 text-xs mt-1">9-Module Life Assessment</p>
            <Progress value={(completedModules.length + extraCompleted) / (9 + extraTotal) * 100} className="h-1.5 mt-2" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-divine-gold">B. DIAGNOSTIC RESULTS</h2>
              <span className="text-chakra-crown/50 text-xs">{completedModules.length + extraCompleted}/{9 + extraTotal} scored</span>
            </div>
            <p className="text-chakra-crown/50 text-xs mt-1">Computed analysis from assessments</p>
            <Progress value={(completedModules.length + extraCompleted) / (9 + extraTotal) * 100} className="h-1.5 mt-2" />
          </div>
        </div>

        {/* Paired rows — one per module */}
        {modules.map(m => {
          const isCompleted = completedModules.includes(m.id);
          const score = scores[String(m.id)];
          return (
            <div key={m.id} className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" data-testid={`paired-row-${m.id}`}>
              <ModuleCard module={m} isCompleted={isCompleted}
                onClick={() => { setActiveModule(m.id); setTestView("quiz"); loadQuestions(m.id); setTimeout(() => diagnosticRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }}
                onRetake={handleRetakeModule}
              />
              {isCompleted && score ? (
                <ScoreCard module={m} score={score} />
              ) : (
                <div className="p-4 rounded-xl border border-divine-gold/5 bg-cosmic-light/3 flex items-center gap-3" data-testid={`result-placeholder-${m.id}`}>
                  <Lock className="w-5 h-5 text-chakra-crown/20 shrink-0" />
                  <p className="text-chakra-crown/30 text-sm">Complete the test to see results</p>
                </div>
              )}
            </div>
          );
        })}

        {/* Overall Assessment (when all 9 done) */}
        {completedModules.length >= 9 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-4 border-t border-divine-gold/20" data-testid="full-results">
            <h3 className="font-heading text-lg text-divine-gold">OVERALL ASSESSMENT</h3>
            {risk && (
              <div className={`p-4 rounded-xl border ${RISK_STYLES[risk.risk]?.bg} ${RISK_STYLES[risk.risk]?.border}`} data-testid="risk-banner">
                <div className="flex items-center gap-3">
                  {React.createElement(RISK_STYLES[risk.risk]?.icon || AlertTriangle, { className: `w-5 h-5 ${RISK_STYLES[risk.risk]?.text}` })}
                  <div>
                    <p className={`font-heading text-sm ${RISK_STYLES[risk.risk]?.text}`}>{risk.label}</p>
                    {risk.low_principles?.length > 0 && <p className="text-xs text-chakra-crown/50 mt-1">Low areas: {risk.low_principles.join(", ")}</p>}
                  </div>
                </div>
              </div>
            )}
            {recommendations?.length === 0 && (
              <div className="text-center py-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-emerald-400 font-heading text-sm">All sections in healthy range!</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    );
  }

  // MODE: "results" — completed scores + risk + overall
  if (mode === "results") {
    if (completedModules.length === 0) {
      return (
        <div className="text-center py-10">
          <Target className="w-10 h-10 text-divine-gold/20 mx-auto mb-3" />
          <p className="text-chakra-crown/50">Complete the Diagnostic Test to see your results</p>
          <p className="text-chakra-crown/30 text-sm mt-1">Go to the Diagnostic Test section in the Awareness tab</p>
        </div>
      );
    }
    return (
      <div className="space-y-5" data-testid="life-diagnostic-results">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl text-divine-gold">DIAGNOSTIC RESULTS</h2>
          <span className="text-chakra-crown/50 text-sm">{completedModules.length + extraCompleted}/{9 + extraTotal} modules completed</span>
        </div>
        <Progress value={(completedModules.length + extraCompleted) / (9 + extraTotal) * 100} className="h-2" />

        {/* Score Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {modules.filter(m => completedModules.includes(m.id)).map(m => (
            <ScoreCard key={m.id} module={m} score={scores[String(m.id)]} />
          ))}
        </div>

        {/* Overall Results (when all 9 done) */}
        {completedModules.length >= 9 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4 pt-4 border-t border-divine-gold/20" data-testid="full-results">
            <h3 className="font-heading text-lg text-divine-gold">OVERALL ASSESSMENT</h3>
            {risk && (
              <div className={`p-4 rounded-xl border ${RISK_STYLES[risk.risk]?.bg} ${RISK_STYLES[risk.risk]?.border}`} data-testid="risk-banner">
                <div className="flex items-center gap-3">
                  {React.createElement(RISK_STYLES[risk.risk]?.icon || AlertTriangle, { className: `w-5 h-5 ${RISK_STYLES[risk.risk]?.text}` })}
                  <div>
                    <p className={`font-heading text-sm ${RISK_STYLES[risk.risk]?.text}`}>{risk.label}</p>
                    {risk.low_principles?.length > 0 && <p className="text-xs text-chakra-crown/50 mt-1">Low areas: {risk.low_principles.join(", ")}</p>}
                  </div>
                </div>
              </div>
            )}
            {recommendations?.length === 0 && (
              <div className="text-center py-6">
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-emerald-400 font-heading">All sections in healthy range!</p>
                <p className="text-chakra-crown/50 text-sm mt-1">Continue your current practices and consult your coach for deeper alignment.</p>
              </div>
            )}
          </motion.div>
        )}
      </div>
    );
  }

  // MODE: "recommendations" — text-based practice recommendations + coaching guidance
  if (mode === "recommendations") {
    const completedRecs = completedModules.filter(id => moduleRecommendations[String(id)]);
    if (completedRecs.length === 0 && recommendations.length === 0) {
      return (
        <div className="text-center py-10">
          <BookOpen className="w-10 h-10 text-divine-gold/20 mx-auto mb-3" />
          <p className="text-chakra-crown/50">Practice recommendations will appear here</p>
          <p className="text-chakra-crown/30 text-sm mt-1">Complete the Diagnostic Test to get personalized recommendations</p>
        </div>
      );
    }
    return (
      <div className="space-y-4" data-testid="life-diagnostic-recommendations">
        {/* Personalized Coaching Guidance (moved from Results) */}
        {recommendations?.length > 0 && (
          <div data-testid="coaching-guidance">
            <h3 className="font-heading text-base text-divine-gold mb-3">PERSONALIZED COACHING GUIDANCE</h3>
            <div className="space-y-2">
              {recommendations.map((r, i) => {
                const mod = modules.find(m => m.id === r.module);
                return (
                  <div key={i} className="p-3 rounded-lg bg-cosmic-light/5 border border-divine-gold/10">
                    <span className="text-divine-gold text-xs font-heading">{mod?.name || `Module ${r.module}`}</span>
                    <p className="text-chakra-crown/70 text-sm mt-1">{r.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Module-specific practice recommendations */}
        {completedRecs.length > 0 && (
          <div>
            <h3 className="font-heading text-base text-divine-gold mb-3">PRACTICE RECOMMENDATIONS</h3>
            <p className="text-chakra-crown/50 text-sm mb-3">Personalized guidance based on your diagnostic results. Actual practice assignments are done by your coach.</p>
            {completedRecs.map(mid => {
              const data = moduleRecommendations[String(mid)];
              return (
                <div key={mid} className="sacred-card mb-3">
                  <h4 className="font-heading text-sm text-divine-gold mb-3">{data.title}</h4>
                  <div className="space-y-2">
                    {data.practices.map((p, i) => (
                      <div key={i} className="p-3 rounded-lg bg-cosmic-deep/50 border border-divine-gold/5">
                        <div className="flex items-start gap-2">
                          <BookOpen className="w-4 h-4 text-divine-gold mt-0.5 shrink-0" />
                          <div>
                            <p className="text-divine-gold text-sm font-medium">{p.title}</p>
                            <p className="text-chakra-crown/60 text-xs mt-1 leading-relaxed">{p.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // MODE: "all" (default, legacy) — full view
  return (
    <div className="space-y-5" data-testid="life-diagnostic" ref={diagnosticRef}>
      <p className="text-chakra-crown/50 text-sm">Use the Awareness and Alignment tabs above for the organized experience.</p>
    </div>
  );
}
